/**
 * UserWebSocket - Global User Session WebSocket Client
 *
 * Hub-and-Spoke architecture: Single WebSocket per user that multiplexes
 * across multiple streams. Provides instant stream switching (no TCP/TLS
 * teardown) and real-time cross-stream notifications.
 *
 * Features:
 * - Subscribe/unsubscribe to streams via messages (not connections)
 * - Delta sync: requests events since lastSeq per stream
 * - Automatic reconnection with exponential backoff
 * - Attention updates pushed in real-time (no 60s polling)
 */

import { axiom } from './axiom';
import * as Sentry from '@sentry/react';
import { api } from './api';
import type { StreamEvent, ObservabilityEvent } from '../types/api';

export type WebSocketStatus = 'connecting' | 'connected' | 'disconnected' | 'error';

const INITIAL_RETRY_DELAY_MS = 1000;
const MAX_RETRY_DELAY_MS = 30000;
const RETRY_BACKOFF_FACTOR = 2;

// Deploy reconnect uses faster backoff since it's a known transient issue
const DEPLOY_INITIAL_RETRY_DELAY_MS = 500;
const DEPLOY_MAX_RETRY_DELAY_MS = 5000;
const DEPLOY_MAX_ATTEMPTS = 5;

// Close codes that indicate a deploy-related disconnect
const DEPLOY_CLOSE_CODES = [1006, 1011, 1012];

// Message types sent to server
export interface SubscribeMessage {
  type: 'subscribe';
  streamId: string;
  lastSeq?: number;
}

export interface UnsubscribeMessage {
  type: 'unsubscribe';
  streamId: string;
}

export interface SendMessageMessage {
  type: 'send_message';
  streamId: string;
  message: string;
  node_id?: string;
  request_id: string;
}

export interface PingMessage {
  type: 'ping';
}

export interface ApproveMessage {
  type: 'approve';
  streamId: string;
  toolCallId: string;
}

export interface RejectMessage {
  type: 'reject';
  streamId: string;
  toolCallId: string;
  reason?: string;
}

export interface CancelMessage {
  type: 'cancel';
  streamId: string;
  target: 'agent_loop';
  targetId?: string;
}

/** Enable Glass Box observability for a stream (Spec 124 Phase 1b) */
export interface EnableObservabilityMessage {
  type: 'enable_observability';
  streamId: string;
}

/** Disable Glass Box observability for a stream (Spec 124 Phase 1b) */
export interface DisableObservabilityMessage {
  type: 'disable_observability';
  streamId: string;
}

export type OutgoingMessage =
  | SubscribeMessage
  | UnsubscribeMessage
  | SendMessageMessage
  | ApproveMessage
  | RejectMessage
  | CancelMessage
  | EnableObservabilityMessage
  | DisableObservabilityMessage
  | PingMessage;

// Sentry trace propagation envelope
interface SentryTraceEnvelope {
  _sentry?: {
    trace: string;
    baggage?: string;
  };
}

// Message types received from server
export interface StreamEventMessage {
  source: string;  // streamId
  type: string;
  seq?: number;
  buffered?: boolean;  // True if event was buffered during offline
  [key: string]: unknown;
}

export interface SubscribedMessage {
  type: 'subscribed';
  streamId: string;
  lastSeq: number;
}

export interface ConnectedMessage {
  type: 'connected';
  userEmail: string;
}

export interface ErrorMessage {
  type: 'error';
  code: string;
  message: string;
  streamId?: string;
  source?: string;  // Alternative stream identifier when error is broadcast via stream
  recoverable?: boolean;  // Whether the error is recoverable (user can retry)
}

export interface HardRefreshMessage {
  type: 'hard-refresh-required';
  streamId: string;
  message: string;
}

// Event handlers
export interface UserWebSocketEvents {
  statusChange: (status: WebSocketStatus) => void;
  streamEvent: (streamId: string, event: StreamEvent) => void;
  subscribed: (streamId: string, lastSeq: number) => void;
  error: (error: { code: string; message: string; recoverable?: boolean }, streamId?: string) => void;
  hardRefreshRequired: (streamId: string) => void;
  /** Emitted when a deploy-related disconnect triggers fast reconnect */
  deployReconnecting: (attempt: number, maxAttempts: number) => void;
  /** Emitted when deploy reconnect exhausts all attempts */
  deployReconnectFailed: () => void;
  /** Emitted when a warning message is received (e.g., SUBSCRIPTION_DEGRADED) */
  warning: (code: string, message: string, streamId?: string) => void;
  /** Emitted when an observability event is received (Glass Box UI, Spec 124 Phase 1b) */
  observabilityEvent: (streamId: string, event: ObservabilityEvent) => void;
}

export class UserWebSocket {
  private ws: WebSocket | null = null;
  private status: WebSocketStatus = 'disconnected';
  private reconnectAttempts = 0;
  private reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
  // EventEmitter pattern: multiple handlers per event type
  private listeners: Map<keyof UserWebSocketEvents, Set<UserWebSocketEvents[keyof UserWebSocketEvents]>> = new Map();
  private shouldReconnect = true;
  private baseUrl: string;

  // Track subscriptions with their last sequence numbers
  private subscriptions: Map<string, number> = new Map();

  // Spec 167 Phase 2: observabilitySubscriptions removed — observability is always-on.

  // Deploy reconnect tracking
  private isDeployReconnect = false;
  private deployReconnectAttempts = 0;

  // ADR-031: Connection tracking for observability
  private connectionId: string | null = null;
  private connectionStartTime: number | null = null;
  private lastPongTime: number | null = null;
  private lastPingSentTime: number | null = null;

  // ADR-031: Browser state listener references for cleanup
  private visibilityChangeHandler: (() => void) | null = null;
  private onlineHandler: (() => void) | null = null;
  private offlineHandler: (() => void) | null = null;

  /**
   * Test mode email - when set, uses test_email query param instead of ticket auth.
   * @see docs/specs/044-integration-testing-framework.md
   */
  private testEmail: string | undefined;

  /**
   * @param testEmail Optional test email for integration testing. When provided,
   *                  bypasses ticket auth and uses test_email query param on WebSocket URL.
   */
  constructor(testEmail?: string) {
    this.testEmail = testEmail;

    const apiUrl = import.meta.env.VITE_API_URL || '/api';
    if (apiUrl.startsWith('http://')) {
      this.baseUrl = apiUrl.replace('http://', 'ws://');
    } else if (apiUrl.startsWith('https://')) {
      this.baseUrl = apiUrl.replace('https://', 'wss://');
    } else {
      this.baseUrl = window.location.protocol === 'https:' ? 'wss://' : 'ws://';
      this.baseUrl += window.location.host + apiUrl;
    }

    // ADR-031: Setup browser state listeners for observability
    this.setupBrowserStateListeners();
  }

  /**
   * ADR-031: Setup browser state listeners to correlate connection issues
   * with browser visibility changes and online/offline events.
   */
  private setupBrowserStateListeners(): void {
    if (typeof document !== 'undefined') {
      this.visibilityChangeHandler = () => {
        axiom.info('ws_browser_state', {
          event: 'visibility_change',
          visible: !document.hidden,
          online: typeof navigator !== 'undefined' ? navigator.onLine : null,
          connection_state: this.status,
          connection_id: this.connectionId,
        });
      };
      document.addEventListener('visibilitychange', this.visibilityChangeHandler);
    }

    if (typeof window !== 'undefined') {
      this.onlineHandler = () => {
        axiom.info('ws_browser_state', {
          event: 'online_change',
          visible: typeof document !== 'undefined' ? !document.hidden : null,
          online: true,
          connection_state: this.status,
          connection_id: this.connectionId,
        });

        // Reconnect if disconnected OR if the current WebSocket is stale (not OPEN).
        // The stale-WS case handles a race specific to Playwright offline simulation:
        // setOffline(true) then ws.close(4000) puts the WS into CLOSING state, but
        // Chromium only fires the 'close' event after the TCP timeout (~30s) because
        // the close handshake can't complete on a blocked network. The 500ms wait in
        // goOffline() times out, so when the 'online' event fires, status is still
        // 'connected' but the WS is stuck in CLOSING and cannot send messages.
        const wsIsStale = this.ws !== null && this.ws.readyState !== WebSocket.OPEN;
        if ((this.status === 'disconnected' || this.status === 'error' || wsIsStale) && this.shouldReconnect) {
          axiom.info('ws_online_reconnect', {
            event: 'ws_online_reconnect_triggered',
            pending_timer: !!this.reconnectTimeout,
            reconnect_attempts: this.reconnectAttempts,
            connection_id: this.connectionId,
            ws_ready_state: this.ws?.readyState ?? null,
            triggered_by: wsIsStale ? 'stale_ws' : 'disconnected_status',
          });
          // Clear any pending backoff timer so it doesn't double-connect
          if (this.reconnectTimeout) {
            clearTimeout(this.reconnectTimeout);
            this.reconnectTimeout = null;
          }
          // Detach handlers from the stale WS to prevent its eventual 'close' event
          // (which fires after Chromium's TCP timeout) from scheduling another reconnect
          // after we've already established a fresh connection.
          if (wsIsStale && this.ws) {
            this.ws.onclose = null;
            this.ws.onerror = null;
            this.ws.onopen = null;
            this.ws.onmessage = null;
          }
          this.ws = null;
          this.reconnectAttempts = 0;
          this.setStatus('disconnected');
          this.doConnect();
        }
      };
      window.addEventListener('online', this.onlineHandler);

      this.offlineHandler = () => {
        axiom.info('ws_browser_state', {
          event: 'online_change',
          visible: typeof document !== 'undefined' ? !document.hidden : null,
          online: false,
          connection_state: this.status,
          connection_id: this.connectionId,
        });
      };
      window.addEventListener('offline', this.offlineHandler);
    }
  }

  /**
   * ADR-031: Remove browser state listeners to prevent memory leaks.
   */
  private teardownBrowserStateListeners(): void {
    if (typeof document !== 'undefined' && this.visibilityChangeHandler) {
      document.removeEventListener('visibilitychange', this.visibilityChangeHandler);
      this.visibilityChangeHandler = null;
    }

    if (typeof window !== 'undefined') {
      if (this.onlineHandler) {
        window.removeEventListener('online', this.onlineHandler);
        this.onlineHandler = null;
      }
      if (this.offlineHandler) {
        window.removeEventListener('offline', this.offlineHandler);
        this.offlineHandler = null;
      }
    }
  }

  /**
   * Register an event handler.
   * Multiple handlers can be registered for the same event type.
   */
  on<K extends keyof UserWebSocketEvents>(event: K, handler: UserWebSocketEvents[K]): void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)!.add(handler);
  }

  /**
   * Remove an event handler.
   * If handler is provided, removes only that specific handler.
   * If handler is omitted, removes all handlers for the event type (backwards compat).
   */
  off<K extends keyof UserWebSocketEvents>(event: K, handler?: UserWebSocketEvents[K]): void {
    if (handler) {
      // Remove specific handler
      const handlers = this.listeners.get(event);
      if (handlers) {
        handlers.delete(handler);
        // Clean up empty Set to prevent memory leak
        if (handlers.size === 0) {
          this.listeners.delete(event);
        }
      }
    } else {
      // Remove all handlers for this event type (backwards compat)
      this.listeners.delete(event);
    }
  }

  getStatus(): WebSocketStatus {
    return this.status;
  }

  /**
   * Get the raw WebSocket instance for testing.
   * Only use this in test environments to simulate network failures.
   * @internal
   */
  getRawWebSocket(): WebSocket | null {
    return this.ws;
  }

  getSubscriptions(): Map<string, number> {
    return new Map(this.subscriptions);
  }

  isSubscribed(streamId: string): boolean {
    return this.subscriptions.has(streamId);
  }

  getLastSeq(streamId: string): number {
    return this.subscriptions.get(streamId) || 0;
  }

  async connect(): Promise<void> {
    if (this.ws && (this.ws.readyState === WebSocket.CONNECTING || this.ws.readyState === WebSocket.OPEN)) {
      axiom.warn('WebSocket already connected', { event: 'ws_already_connected' });
      return;
    }
    this.shouldReconnect = true;
    await this.doConnect();
  }

  private async doConnect(): Promise<void> {
    try {
      this.setStatus('connecting');

      let wsUrl: string;
      if (this.testEmail) {
        // Test mode: connect directly with test email as query param
        // Backend (DEV_MODE) extracts email from X-Test-User-Email header or query param
        wsUrl = `${this.baseUrl}/ws/user?test_email=${encodeURIComponent(this.testEmail)}`;
        axiom.info('WebSocket connecting (test mode)', { event: 'ws_connecting', testEmail: this.testEmail });
      } else {
        // Production mode: get ticket from API
        const ticketResponse = await api.websocket.getUserTicket();
        wsUrl = `${this.baseUrl}/ws/user?ticket=${ticketResponse.ticket}`;
        axiom.info('WebSocket connecting', { event: 'ws_connecting' });
      }

      // Guard: if disconnect() was called while awaiting getUserTicket(), abort
      // to prevent creating a WebSocket after the user has intentionally disconnected.
      if (!this.shouldReconnect) {
        axiom.info("ws_lifecycle", { event: "ws_connect_aborted_after_ticket", connection_id: this.connectionId });
        return;
      }

      this.ws = new WebSocket(wsUrl);
      this.ws.onopen = this.handleOpen.bind(this);
      this.ws.onmessage = this.handleMessage.bind(this);
      this.ws.onerror = this.handleError.bind(this);
      this.ws.onclose = this.handleClose.bind(this);
    } catch (error) {
      axiom.error('WebSocket connection failed', { event: 'ws_connect_failed', error: error instanceof Error ? error.message : String(error) });
      this.setStatus('error');
      const errorMsg = error instanceof Error ? error.message : 'Connection failed';
      this.emit('error', { code: 'CONNECTION_FAILED', message: errorMsg, recoverable: true });
      this.scheduleReconnect();
    }
  }

  disconnect(): void {
    axiom.info('WebSocket disconnecting', { event: 'ws_disconnecting' });
    this.shouldReconnect = false;
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
    if (this.ws) {
      this.ws.close(1000, 'Client disconnect');
      this.ws = null;
    }
    this.subscriptions.clear();
    this.setStatus('disconnected');

    // ADR-031: Clean up browser state listeners to prevent memory leaks
    this.teardownBrowserStateListeners();
  }

  /**
   * Subscribe to a stream's events.
   * If lastSeq is provided, the server will send catch-up events since that sequence.
   *
   * If not connected, stores the subscription and sends it when connected.
   * This allows subscribing before the WebSocket is ready.
   */
  subscribe(streamId: string, lastSeq = 0): void {
    // Dedup: skip if already subscribed with same or higher lastSeq.
    // Makes subscribe() idempotent — safe to call multiple times without
    // generating redundant server RPCs. Higher lastSeq (delta sync) passes through.
    const existingSeq = this.subscriptions.get(streamId);
    if (existingSeq !== undefined && lastSeq <= existingSeq) {
      axiom.info('WebSocket subscribe deduped', {
        event: 'ws_subscribe_dedup',
        stream_id: streamId,
        existing_seq: existingSeq,
        requested_seq: lastSeq,
      });
      return;
    }

    // Always store the subscription so it's sent on connect/reconnect
    this.subscriptions.set(streamId, lastSeq);

    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      axiom.info(`WebSocket queued subscription`, { event: 'ws_queue_subscribe', stream_id: streamId });
      return;
    }

    axiom.info(`WebSocket subscribing to stream`, { event: 'ws_subscribe', stream_id: streamId, last_seq: lastSeq });
    this.send({ type: 'subscribe', streamId, lastSeq });
  }

  /**
   * Unsubscribe from a stream's events.
   */
  unsubscribe(streamId: string): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      axiom.warn('WebSocket cannot unsubscribe - not connected', { event: 'ws_unsubscribe_failed_disconnected' });
      this.subscriptions.delete(streamId);
      return;
    }

    axiom.info('WebSocket unsubscribing from stream', { event: 'ws_unsubscribe', stream_id: streamId });
    this.send({ type: 'unsubscribe', streamId });
    this.subscriptions.delete(streamId);
  }

  /**
   * Send a message to a stream (e.g., user chat message).
   * @returns true if message was sent, false if WebSocket is not connected
   */
  sendMessage(streamId: string, message: string, nodeId?: string): boolean {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      axiom.warn('WebSocket cannot send message - not connected', { event: 'ws_send_failed_disconnected' });
      return false;
    }

    const requestId = crypto.randomUUID();
    this.send({ type: 'send_message', streamId, message, node_id: nodeId, request_id: requestId });
    return true;
  }

  /**
   * Approve a pending tool call.
   */
  approveToolCall(streamId: string, toolCallId: string): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      axiom.warn('WebSocket cannot approve - not connected', { event: 'ws_approve_failed_disconnected' });
      return;
    }
    this.send({ type: 'approve', streamId, toolCallId });
  }

  /**
   * Reject a pending tool call.
   */
  rejectToolCall(streamId: string, toolCallId: string, reason?: string): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      axiom.warn('WebSocket cannot reject - not connected', { event: 'ws_reject_failed_disconnected' });
      return;
    }
    this.send({ type: 'reject', streamId, toolCallId, reason });
  }

  /**
   * Cancel the current agent loop.
   */
  cancelAgentLoop(streamId: string, nodeId?: string): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      axiom.warn('WebSocket cannot cancel - not connected', { event: 'ws_cancel_failed_disconnected' });
      return;
    }
    this.send({ type: 'cancel', streamId, target: 'agent_loop', targetId: nodeId });
  }

  /**
   * Spec 167 Phase 2: enableObservability/disableObservability removed.
   * Observability is always-on — events are broadcast to all subscribers
   * without explicit opt-in from the frontend.
   */

  ping(): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.lastPingSentTime = Date.now();
      this.send({ type: 'ping' });

      // ADR-031: Log heartbeat ping with connection tracking
      axiom.info('ws_heartbeat', {
        event: 'ping_sent',
        connection_id: this.connectionId,
        last_pong_age_ms: this.lastPongTime ? Date.now() - this.lastPongTime : null,
      });
    }
  }

  private handleOpen(): void {
    // Clear any pending reconnect timer — handles the race where handleClose fires
    // after an online-event-triggered reconnect already succeeded, which would
    // schedule a second doConnect() and create a duplicate connection.
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    // ADR-031: Track connection lifecycle
    this.connectionId = crypto.randomUUID();
    this.connectionStartTime = Date.now();
    this.lastPongTime = null;
    this.lastPingSentTime = null;

    axiom.info('WebSocket connected', { event: 'ws_connected' });

    // ADR-031: Log connection open with tracking ID
    axiom.info('ws_lifecycle', {
      event: 'open',
      connection_id: this.connectionId,
    });

    this.reconnectAttempts = 0;

    // Reset deploy reconnect state on successful connection
    if (this.isDeployReconnect || this.deployReconnectAttempts > 0) {
      axiom.info('WebSocket deploy reconnect succeeded', {
        event: 'ws_deploy_reconnect_success',
        attempts: this.deployReconnectAttempts,
      });
    }
    this.isDeployReconnect = false;
    this.deployReconnectAttempts = 0;

    this.setStatus('connected');

    // Re-subscribe to all previously subscribed streams on reconnect
    for (const [streamId, lastSeq] of this.subscriptions) {
      axiom.info('WebSocket re-subscribing to stream', { event: 'ws_resubscribe', stream_id: streamId });
      this.send({ type: 'subscribe', streamId, lastSeq });
    }

    // Spec 167 Phase 2: observability re-enable on reconnect removed — always-on.
    // Events flow to all subscribers without explicit enable message.
  }

  private handleMessage(event: MessageEvent): void {
    try {
      // Defensive: handle binary frames (Blob/ArrayBuffer) from server.
      // The Rust DO fix uses send_with_str, but this guards against regression.
      // Binary WebSocket frames arrive as Blob (browser default) or ArrayBuffer.
      if (typeof event.data !== 'string') {
        const data = event.data;
        try {
          // Try TextDecoder first — works for ArrayBuffer, TypedArray, DataView.
          // Avoids instanceof checks which fail across realms (Node ↔ jsdom).
          const text = new TextDecoder().decode(data as BufferSource);
          this.handleMessage(new MessageEvent('message', { data: text }));
        } catch {
          if (data && typeof data.text === 'function') {
            // Blob — read as text asynchronously, then re-process
            (data as Blob).text().then(text => {
              this.handleMessage(new MessageEvent('message', { data: text }));
            }).catch(err => {
              axiom.error('ws_binary_read_failed', { event: 'ws_binary_read_failed', error: String(err) });
            });
          } else {
            axiom.error('ws_unsupported_data_type', {
              event: 'ws_unsupported_data_type',
              data_type: typeof data,
              constructor: data?.constructor?.name,
            });
          }
        }
        return;
      }

      const message = JSON.parse(event.data);

      // ADR-031 Phase 2: Guard against non-object payloads (e.g., JSON primitives)
      if (!message || typeof message !== 'object') {
        axiom.warn('ws_message', {
          event: 'received_invalid',
          connection_id: this.connectionId,
          payload_type: typeof message,
        });
        return;
      }

      // ADR-031 Phase 2: Track all received messages
      axiom.info('ws_message', {
        event: 'received',
        message_type: message.type,
        connection_id: this.connectionId,
        source: 'source' in message ? message.source : null,
      });
      // Handle protocol messages
      if (message.type === 'connected') {
        axiom.info('WebSocket registered', { event: 'ws_registered', user_email: message.userEmail });
        return;
      }

      if (message.type === 'pong') {
        // ADR-031: Log heartbeat pong with round-trip time
        const roundTripMs = this.lastPingSentTime ? Date.now() - this.lastPingSentTime : null;
        this.lastPongTime = Date.now();
        axiom.info('ws_heartbeat', {
          event: 'pong_received',
          connection_id: this.connectionId,
          round_trip_ms: roundTripMs,
        });
        return;
      }

      if (message.type === 'subscribed') {
        const { streamId, lastSeq } = message as SubscribedMessage;
        axiom.info(`WebSocket subscribed to stream`, { event: 'ws_subscribed', stream_id: streamId, last_seq: lastSeq });
        this.subscriptions.set(streamId, lastSeq);
        this.emit('subscribed', streamId, lastSeq);
        return;
      }

      if (message.type === 'error') {
        const { code, message: errorMsg, streamId, source, recoverable } = message as ErrorMessage;
        // Errors can come with streamId field OR source field (when broadcast via stream)
        const effectiveStreamId = streamId || source;

        // ADR-031 Phase 2: Track error responses for correlation
        axiom.info('ws_message', {
          event: 'error_received',
          error_code: code,
          message_type: 'error',
          connection_id: this.connectionId,
          stream_id: effectiveStreamId,
        });

        axiom.error('WebSocket error received', { event: 'ws_error_msg', code, message: errorMsg, stream_id: effectiveStreamId });

        // Handle DO version mismatch - server is being deployed
        if (code === 'DO_VERSION_MISMATCH') {
          axiom.warn('WebSocket DO version mismatch - triggering deploy reconnect', { event: 'ws_do_version_mismatch' });
          this.isDeployReconnect = true;
          // Don't emit error for version mismatch - let the close handler trigger reconnect
          return;
        }

        // Emit error with full structure including code, message, and recoverable fields
        // Bug fix: Previously emitted generic Error object which lost the error metadata
        const errorEvent = { code, message: errorMsg, recoverable };
        this.emit('error', errorEvent, effectiveStreamId);
        return;
      }

      if (message.type === 'hard-refresh-required') {
        const { streamId } = message as HardRefreshMessage;
        axiom.warn('WebSocket hard refresh required', { event: 'ws_hard_refresh', stream_id: streamId });
        this.emit('hardRefreshRequired', streamId);
        return;
      }

      // Handle warning messages (e.g., SUBSCRIPTION_DEGRADED from UserSessionDO)
      if (message.type === 'warning') {
        const { code, message: warningMsg, streamId } = message as { type: string; code: string; message: string; streamId?: string };
        axiom.warn('WebSocket warning received', { event: 'ws_warning_msg', code, message: warningMsg, stream_id: streamId });
        this.emit('warning', code, warningMsg, streamId);
        return;
      }

      // Handle observability events (Glass Box UI, Spec 124 Phase 1b)
      // Must be checked before the generic 'source' branch since observability events
      // also carry a 'source' field but are NOT StreamEvents.
      if (message.type === 'observability' && 'source' in message && message.source) {
        const streamId = message.source as string;
        const event = message.event as string;
        const data = (message.data ?? {}) as Record<string, unknown>;
        axiom.info('ws_observability_event', {
          event: 'observability_received',
          sub_event: event,
          stream_id: streamId,
          connection_id: this.connectionId,
        });
        this.emit('observabilityEvent', streamId, { event, data });
        return;
      }

      // Handle stream events (have 'source' field indicating the stream)
      if ('source' in message && message.source) {
        const streamId = message.source as string;
        const streamEvent = this.extractStreamEvent(message);

        if (streamEvent) {
          // Update lastSeq for this stream
          if (streamEvent.seq !== undefined) {
            const currentSeq = this.subscriptions.get(streamId) || 0;
            if (streamEvent.seq > currentSeq) {
              this.subscriptions.set(streamId, streamEvent.seq);
            }
          }

          if (message.buffered) {
            axiom.info(`WebSocket buffered event`, { event: 'ws_buffered_event', stream_id: streamId, event_type: streamEvent.type });
          }

          this.emit('streamEvent', streamId, streamEvent);
        }
        return;
      }

      // Unknown message type - check if it looks like a stream event missing source
      // Note: 'error' is excluded as it's already handled above at line 546
      const msgType = (message as { type?: string }).type;
      const knownStreamEventTypes = [
        // Graph events
        'node_created', 'node_updated', 'node_deleted',
        'edge_created', 'edge_deleted', 'assistant_chunk',
        // Chat/workflow events
        'agent_started', 'content', 'tool_requested', 'tool_executed',
        'agent_complete', 'agent_cancelled', 'tool_approval_response',
        'workflow_started', 'workflow_terminated', 'node_status_changed', 'agent_iteration',
        'message_received',
        // Adaptive thinking events (Spec 144) — keep aligned with eventTypes allowlist below
        'thinking',
      ];
      if (msgType && knownStreamEventTypes.includes(msgType)) {
        // This is a stream event but missing 'source' field - indicates backend bug
        axiom.error('WebSocket stream event missing source field', {
          event: 'ws_event_missing_source',
          event_type: msgType,
          has_source_key: 'source' in message,
          source_value: (message as { source?: unknown }).source,
          message,
        });
      } else {
        axiom.warn('WebSocket unknown message', { event: 'ws_unknown_msg', message });
      }
    } catch (error) {
      axiom.error('WebSocket failed to parse message', { event: 'ws_parse_failed', error: error instanceof Error ? error.message : String(error) });
    }
  }

  private handleError(event: Event): void {
    axiom.error('WebSocket error event', { event: 'ws_error_event', error: event });
    this.setStatus('error');
  }

  private handleClose(event: CloseEvent): void {
    // Guard: ignore close events from stale WebSockets that have already been replaced.
    // This happens when the online event handler detects a CLOSING socket and creates
    // a new connection (setting this.ws to the new socket). The old socket's deferred
    // 'close' event (delayed by Chromium's TCP timeout) would otherwise schedule a
    // second reconnect and disrupt the already-established new connection.
    const closingWs = event.target as WebSocket | null;
    if (closingWs !== null && this.ws !== null && closingWs !== this.ws) {
      axiom.info('ws_lifecycle', {
        event: 'stale_close_ignored',
        connection_id: this.connectionId,
        close_code: event.code,
      });
      return;
    }

    // ADR-031: Log connection close with lifecycle metrics
    axiom.info('ws_lifecycle', {
      event: 'close',
      connection_id: this.connectionId,
      connection_age_ms: this.connectionStartTime ? Date.now() - this.connectionStartTime : null,
      close_code: event.code,
      close_reason: event.reason || null,
      was_clean: event.wasClean,
    });

    axiom.info(`WebSocket closed`, { event: 'ws_closed', code: event.code, reason: event.reason });
    this.ws = null;
    this.setStatus('disconnected');

    if (!this.shouldReconnect || event.code === 1000) {
      return;
    }

    // Check if this is a deploy-related disconnect
    const isDeployClose = DEPLOY_CLOSE_CODES.includes(event.code) || this.isDeployReconnect;

    if (isDeployClose) {
      axiom.info('WebSocket deploy-related disconnect detected', {
        event: 'ws_deploy_disconnect',
        code: event.code,
        is_version_mismatch: this.isDeployReconnect,
      });
      this.scheduleDeployReconnect();
    } else {
      this.scheduleReconnect();
    }
  }

  private scheduleReconnect(): void {
    if (!this.shouldReconnect) return;
    const delay = Math.min(
      INITIAL_RETRY_DELAY_MS * Math.pow(RETRY_BACKOFF_FACTOR, this.reconnectAttempts),
      MAX_RETRY_DELAY_MS
    );
    axiom.info(`WebSocket reconnecting`, { event: 'ws_reconnecting', delay_ms: delay, attempt: this.reconnectAttempts + 1 });
    this.reconnectTimeout = setTimeout(() => {
      this.reconnectAttempts++;
      this.doConnect();
    }, delay);
  }

  /**
   * Schedule a fast reconnect for deploy-related disconnects.
   * Uses shorter delays and limited attempts since deploy issues are transient.
   */
  private scheduleDeployReconnect(): void {
    if (!this.shouldReconnect) return;

    // Check if we've exhausted deploy reconnect attempts
    if (this.deployReconnectAttempts >= DEPLOY_MAX_ATTEMPTS) {
      axiom.warn('WebSocket deploy reconnect exhausted', {
        event: 'ws_deploy_reconnect_exhausted',
        attempts: this.deployReconnectAttempts,
      });
      this.emit('deployReconnectFailed');
      // Reset deploy state and fall back to normal reconnect
      this.isDeployReconnect = false;
      this.deployReconnectAttempts = 0;
      this.scheduleReconnect();
      return;
    }

    const delay = Math.min(
      DEPLOY_INITIAL_RETRY_DELAY_MS * Math.pow(RETRY_BACKOFF_FACTOR, this.deployReconnectAttempts),
      DEPLOY_MAX_RETRY_DELAY_MS
    );

    this.deployReconnectAttempts++;
    this.emit('deployReconnecting', this.deployReconnectAttempts, DEPLOY_MAX_ATTEMPTS);

    axiom.info('WebSocket deploy reconnecting', {
      event: 'ws_deploy_reconnecting',
      delay_ms: delay,
      attempt: this.deployReconnectAttempts,
      max_attempts: DEPLOY_MAX_ATTEMPTS,
    });

    this.reconnectTimeout = setTimeout(() => {
      this.doConnect();
    }, delay);
  }

  private send(message: OutgoingMessage): void {
    const messageType = message.type;
    const requestId = 'request_id' in message ? message.request_id : null;

    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      // ADR-031 Phase 2: Track blocked messages
      axiom.info('ws_message', {
        event: 'send_blocked',
        message_type: messageType,
        request_id: requestId,
        connection_id: this.connectionId,
        connection_state: this.ws?.readyState ?? null,
      });
      return;
    }

    try {
      // Inject Sentry trace context for distributed tracing (skip ping messages)
      const messageToSend = this.shouldInjectTrace(message)
        ? this.injectTraceContext(message)
        : message;

      this.ws.send(JSON.stringify(messageToSend));

      // ADR-031 Phase 2: Track successful sends
      axiom.info('ws_message', {
        event: 'send_success',
        message_type: messageType,
        request_id: requestId,
        connection_id: this.connectionId,
      });
    } catch (error) {
      // ADR-031 Phase 2: Track failed sends
      axiom.info('ws_message', {
        event: 'send_failed',
        message_type: messageType,
        request_id: requestId,
        connection_id: this.connectionId,
        error: error instanceof Error ? error.message : String(error),
      });
    }
  }

  /**
   * Inject Sentry trace context into message for distributed tracing.
   * Allows backend to continue the trace started in the frontend.
   */
  private injectTraceContext<T extends object>(payload: T): T & SentryTraceEnvelope {
    try {
      const traceData = Sentry.getTraceData();
      if (traceData['sentry-trace']) {
        return {
          ...payload,
          _sentry: {
            trace: traceData['sentry-trace'],
            baggage: traceData['baggage'],
          },
        };
      }
    } catch {
      // Sentry not initialized or error getting trace data - continue without trace
    }
    return payload;
  }

  /**
   * Determine if we should inject trace context for this message.
   * Skip for ping messages to avoid noise in trace data.
   */
  private shouldInjectTrace(message: OutgoingMessage): boolean {
    return message.type !== 'ping';
  }

  private setStatus(status: WebSocketStatus): void {
    if (this.status !== status) {
      this.status = status;
      this.emit('statusChange', status);
    }
  }

  private emit<K extends keyof UserWebSocketEvents>(
    event: K,
    ...args: Parameters<NonNullable<UserWebSocketEvents[K]>>
  ): void {
    const handlers = this.listeners.get(event);
    if (handlers) {
      for (const handler of handlers) {
        try {
          // @ts-expect-error - TypeScript can't infer the correct signature
          handler(...args);
        } catch (error) {
          axiom.error(`WebSocket handler error`, { event: 'ws_handler_error', handler_event: event, error: error instanceof Error ? error.message : String(error) });
        }
      }
    }
  }

  /**
   * Extract a StreamEvent from a server message.
   * Removes the 'source' field and returns the underlying event.
   */
  private extractStreamEvent(message: StreamEventMessage): StreamEvent | null {
    const eventTypes = [
      // Graph events
      'node_created',
      'node_updated',
      'node_deleted',
      'edge_created',
      'edge_deleted',
      'assistant_chunk',
      // Chat/workflow events
      'agent_started',
      'content',
      'tool_requested',
      'tool_executed',
      'agent_complete',
      'agent_cancelled',
      'error',
      'tool_approval_response',
      'workflow_started',
      'workflow_terminated',
      'node_status_changed',
      'agent_iteration',
      'message_received',
      'thinking',
    ];

    // Note: 'thinking' must be in eventTypes for adaptive thinking events to reach consumers.
    // It was added in PR2 (Spec 144) alongside ThinkingEvent in shared-types.
    if (eventTypes.includes(message.type)) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { source, buffered, ...event } = message;
      return event as unknown as StreamEvent;
    }

    return null;
  }
}
